/**
 * Emergency Plan wizard config — data-driven step definitions.
 *
 * Phase 20 (Refactor 3, supplemented in Phase 20-02). Mirrors
 * `EMERGENCY_PLAN_WIZARD_STEPS` (../wizard-schemas/emergency-plan.ts).
 * 8 content steps + preview.
 *
 * All steps are data-driven — the emergency plan wizard is a long
 * sequence of textareas + a few short text fields. AI section-fill is
 * declared on the longer textareas where the user benefits most from a
 * head-start.
 *
 *   - `context`   → data-driven (3 fields)
 *   - `site`      → data-driven (3 fields)
 *   - `assembly`  → data-driven (text + textarea + AI)
 *   - `contacts`  → data-driven (4 text fields)
 *   - `firstAid`  → data-driven (text + textarea)
 *   - `fire`      → data-driven (text + textarea + AI)
 *   - `spillage`  → data-driven (textarea + AI)
 *   - `reporting` → data-driven (2 textareas)
 *   - `preview`   → custom (`EmergencyPlanPreview`)
 */
import type { WizardConfig } from "./types";

export const EMERGENCY_PLAN_WIZARD_CONFIG: WizardConfig = {
  documentType: "EMERGENCY_PLAN",
  steps: [
    {
      id: "context",
      titleKey: "wizards.emergencyPlan.stepTitles.context",
      descriptionKey: "wizards.emergencyPlan.stepDescriptions.context",
      schemaRef: "EMERGENCY_PLAN.steps.context",
      fields: [
        {
          id: "projectTitle",
          type: "text",
          labelKey: "wizards.emergencyPlan.fields.projectTitle",
          required: true,
        },
        {
          id: "siteAddress",
          type: "text",
          labelKey: "wizards.rams.siteAddressLabel",
          required: true,
        },
        {
          id: "scopeDescription",
          type: "textarea",
          labelKey: "wizards.emergencyPlan.fields.scopeDescription",
          helperKey: "wizards.emergencyPlan.helpers.scopeDescription",
          rows: 3,
        },
      ],
    },
    {
      id: "site",
      titleKey: "wizards.emergencyPlan.stepTitles.site",
      descriptionKey: "wizards.emergencyPlan.stepDescriptions.site",
      schemaRef: "EMERGENCY_PLAN.steps.site",
      fields: [
        {
          id: "sitePostcode",
          type: "text",
          labelKey: "wizards.emergencyPlan.fields.sitePostcode",
          placeholderKey: "wizards.emergencyPlan.placeholders.sitePostcode",
          required: true,
        },
        {
          id: "nearestHospital",
          type: "textarea",
          labelKey: "wizards.emergencyPlan.fields.nearestHospital",
          helperKey: "wizards.emergencyPlan.helpers.nearestHospital",
          required: true,
          rows: 2,
        },
        {
          id: "nearestFireStation",
          type: "textarea",
          labelKey: "wizards.emergencyPlan.fields.nearestFireStation",
          helperKey: "wizards.emergencyPlan.helpers.nearestFireStation",
          rows: 2,
        },
      ],
    },
    {
      id: "assembly",
      titleKey: "wizards.emergencyPlan.stepTitles.assembly",
      descriptionKey: "wizards.emergencyPlan.stepDescriptions.assembly",
      schemaRef: "EMERGENCY_PLAN.steps.assembly",
      fields: [
        {
          id: "assemblyPoint",
          type: "text",
          labelKey: "wizards.emergencyPlan.fields.assemblyPoint",
          placeholderKey: "wizards.emergencyPlan.placeholders.assemblyPoint",
          required: true,
        },
        {
          id: "escapeRoutes",
          type: "textarea",
          labelKey: "wizards.emergencyPlan.fields.escapeRoutes",
          helperKey: "wizards.emergencyPlan.helpers.escapeRoutes",
          required: true,
          rows: 4,
        },
      ],
      aiSectionSuggest: {
        enabled: true,
        documentType: "EMERGENCY_PLAN",
        sectionKey: "assemblyPoint",
        targetFieldId: "assemblyPoint",
      },
    },
    {
      id: "contacts",
      titleKey: "wizards.emergencyPlan.stepTitles.contacts",
      descriptionKey: "wizards.emergencyPlan.stepDescriptions.contacts",
      schemaRef: "EMERGENCY_PLAN.steps.contacts",
      fields: [
        {
          id: "siteManagerContact",
          type: "text",
          labelKey: "wizards.emergencyPlan.fields.siteManagerContact",
          placeholderKey: "wizards.emergencyPlan.placeholders.siteManagerContact",
          required: true,
        },
        {
          id: "principalContractorContact",
          type: "text",
          labelKey: "wizards.emergencyPlan.fields.principalContractorContact",
          placeholderKey:
            "wizards.emergencyPlan.placeholders.principalContractorContact",
        },
        {
          id: "projectManagerContact",
          type: "text",
          labelKey: "wizards.emergencyPlan.fields.projectManagerContact",
          placeholderKey:
            "wizards.emergencyPlan.placeholders.projectManagerContact",
        },
        {
          id: "firstAiderContacts",
          type: "textarea",
          labelKey: "wizards.emergencyPlan.fields.firstAiderContacts",
          helperKey: "wizards.emergencyPlan.helpers.firstAiderContacts",
          required: true,
          rows: 3,
        },
      ],
    },
    {
      id: "firstAid",
      titleKey: "wizards.emergencyPlan.stepTitles.firstAid",
      descriptionKey: "wizards.emergencyPlan.stepDescriptions.firstAid",
      schemaRef: "EMERGENCY_PLAN.steps.firstAid",
      fields: [
        {
          id: "firstAidKitLocation",
          type: "text",
          labelKey: "wizards.emergencyPlan.fields.firstAidKitLocation",
          placeholderKey:
            "wizards.emergencyPlan.placeholders.firstAidKitLocation",
          required: true,
        },
        {
          id: "firstAidArrangements",
          type: "textarea",
          labelKey: "wizards.emergencyPlan.fields.firstAidArrangements",
          helperKey: "wizards.emergencyPlan.helpers.firstAidArrangements",
          required: true,
          rows: 4,
        },
      ],
    },
    {
      id: "fire",
      titleKey: "wizards.emergencyPlan.stepTitles.fire",
      descriptionKey: "wizards.emergencyPlan.stepDescriptions.fire",
      schemaRef: "EMERGENCY_PLAN.steps.fire",
      fields: [
        {
          id: "fireAlarmTestSchedule",
          type: "text",
          labelKey: "wizards.emergencyPlan.fields.fireAlarmTestSchedule",
          placeholderKey:
            "wizards.emergencyPlan.placeholders.fireAlarmTestSchedule",
          required: true,
        },
        {
          id: "fireProcedure",
          type: "textarea",
          labelKey: "wizards.emergencyPlan.fields.fireProcedure",
          placeholderKey: "wizards.emergencyPlan.placeholders.fireProcedure",
          required: true,
          rows: 5,
        },
      ],
      aiSectionSuggest: {
        enabled: true,
        documentType: "EMERGENCY_PLAN",
        sectionKey: "fireProcedure",
        targetFieldId: "fireProcedure",
      },
    },
    {
      id: "spillage",
      titleKey: "wizards.emergencyPlan.stepTitles.spillage",
      descriptionKey: "wizards.emergencyPlan.stepDescriptions.spillage",
      schemaRef: "EMERGENCY_PLAN.steps.spillage",
      fields: [
        {
          id: "spillageProcedure",
          type: "textarea",
          labelKey: "wizards.emergencyPlan.fields.spillageProcedure",
          helperKey: "wizards.emergencyPlan.helpers.spillageProcedure",
          required: true,
          rows: 5,
        },
      ],
      aiSectionSuggest: {
        enabled: true,
        documentType: "EMERGENCY_PLAN",
        sectionKey: "spillageProcedure",
        targetFieldId: "spillageProcedure",
      },
    },
    {
      id: "reporting",
      titleKey: "wizards.emergencyPlan.stepTitles.reporting",
      descriptionKey: "wizards.emergencyPlan.stepDescriptions.reporting",
      schemaRef: "EMERGENCY_PLAN.steps.reporting",
      fields: [
        {
          id: "riddorReporting",
          type: "textarea",
          labelKey: "wizards.emergencyPlan.fields.riddorReporting",
          helperKey: "wizards.emergencyPlan.helpers.riddorReporting",
          required: true,
          rows: 4,
        },
        {
          id: "nearMissReporting",
          type: "textarea",
          labelKey: "wizards.emergencyPlan.fields.nearMissReporting",
          helperKey: "wizards.emergencyPlan.helpers.nearMissReporting",
          required: true,
          rows: 4,
        },
      ],
    },
    {
      id: "preview",
      titleKey: "wizards.emergencyPlan.stepTitles.preview",
      descriptionKey: "wizards.emergencyPlan.stepDescriptions.preview",
      schemaRef: "EMERGENCY_PLAN.steps.preview",
      customRenderer: "EmergencyPlanPreview",
    },
  ],
};
